import { BulletPointsList, Flex, Text, ViewMoreList } from '@/components';
import type { ComponentProps, FC, ReactNode } from 'react';

type CommonProps = {
  term: string;
  termPrefix?: ReactNode;
  items: Array<string | ReactNode>;
  countValues?: boolean;
  thresholdToViewMore?: number;
  extra?: ReactNode;
} & Pick<ComponentProps<typeof ViewMoreList>, 'size' | 'buttonFontSize'>;

type IncludeOpenStateProps = CommonProps & {
  type?: 'include';
  openState?: never;
};

type ExternalOpenStateProps = CommonProps & {
  type?: 'exclude';
  openState: boolean;
};
type DefineTermDescriptionProps =
  | IncludeOpenStateProps
  | ExternalOpenStateProps;

export const DefineTermDescription: FC<DefineTermDescriptionProps> = ({
  term,
  termPrefix,
  items,
  countValues,
  thresholdToViewMore = 4,
  extra,
  size = 'small',
  type = 'include',
  openState,
  buttonFontSize,
}) => {
  const counter = countValues ? `(${items.length})` : '';
  const variant = size === 'medium' ? 'body' : 'footnote';
  const canShowMore = type === 'include';

  const RenderList = () => {
    if (!items.length) {
      return <Text variant={variant} text={undefined} />;
    }

    if (type === 'exclude') {
      if (!openState && !items.length) {
        return <Text variant={variant} text={undefined} />;
      }
      return (
        <BulletPointsList
          list={openState ? items : items.slice(0, thresholdToViewMore)}
        />
      );
    }

    return (
      <ViewMoreList
        dataSource={items}
        threshold={thresholdToViewMore}
        size={size}
        buttonFontSize={buttonFontSize}
        canShowMore={canShowMore}
        initialOpenState={openState}
      />
    );
  };

  return (
    <Flex vertical>
      <Flex>
        {termPrefix}
        <Text variant={variant} color="secondary" text={term + counter} />
        {extra}
      </Flex>

      <RenderList />
    </Flex>
  );
};
